(function () {
  let vehicleTypesMap = null;
  let vehicleTypesPromise = null;

  function loadVehicleTypes(url) {
    if (vehicleTypesMap) return Promise.resolve(vehicleTypesMap);
    if (!url) {
      vehicleTypesMap = {};
      return Promise.resolve(vehicleTypesMap);
    }
    if (vehicleTypesPromise) return vehicleTypesPromise;

    vehicleTypesPromise = fetch(url)
      .then((response) => (response.ok ? response.json() : {}))
      .then((data) => {
        vehicleTypesMap = data && typeof data === 'object' ? data : {};
        return vehicleTypesMap;
      })
      .catch(() => {
        vehicleTypesMap = {};
        return vehicleTypesMap;
      });

    return vehicleTypesPromise;
  }

  function typeValueToSlug(typeValue) {
    return (typeValue || '').trim().replace(/\s+/g, '-').toLowerCase();
  }

  function typesMatch(menuType, availableType) {
    const menu = (menuType || '').trim();
    const available = (availableType || '').trim();
    if (!menu || !available) return false;
    if (menu.toLowerCase() === available.toLowerCase()) return true;

    const menuSlug = typeValueToSlug(menu);
    const availSlug = typeValueToSlug(available);
    if (menuSlug === availSlug) return true;
    if (menuSlug === `${availSlug}s` || availSlug === `${menuSlug}s`) return true;

    const strip = (slug) => slug.replace(/s$/, '');
    return strip(menuSlug) === strip(availSlug);
  }

  function parseAvailableTypes(link) {
    if (link?.dataset && 'availableTypes' in link.dataset) {
      const raw = link.dataset.availableTypes;
      if (raw === '') return [];
      return raw.split('|').map((type) => type.trim()).filter(Boolean);
    }

    if (!vehicleTypesMap) return null;

    const handle = link?.dataset?.collectionHandle?.trim();
    if (!handle || !Object.prototype.hasOwnProperty.call(vehicleTypesMap, handle)) {
      return null;
    }

    const types = vehicleTypesMap[handle];
    return Array.isArray(types) ? types : null;
  }

  function linkHasType(types, typeValue) {
    if (types === null) return true;
    if (!types.length) return false;
    return types.some((type) => typesMatch(typeValue, type));
  }

  function buildCollectionTypeUrl(collectionUrl, typeSlug) {
    if (!collectionUrl || !typeSlug) return collectionUrl || '';

    try {
      const url = new URL(collectionUrl, window.location.origin);
      const match = url.pathname.match(/^(.*\/collections\/[^/]+)/);
      const basePath = match ? match[1] : url.pathname.replace(/\/$/, '');
      return `${basePath}/${typeSlug}`;
    } catch {
      const basePath = collectionUrl.split('?')[0].replace(/\/$/, '');
      return `${basePath}/${typeSlug}`;
    }
  }

  function hydrateDeferredPanels(root) {
    if (!root || root.dataset.panelsHydrated === 'true') return;
    const template = root.querySelector(':scope > template[data-deferred-panels]');
    if (template) {
      root.appendChild(template.content.cloneNode(true));
      template.remove();
    }
    root.dataset.panelsHydrated = 'true';
  }

  function applyMakeLogos(root) {
    const registry = document.getElementById('menu-make-logos');
    if (!registry) return;

    const logos = new Map();
    registry.querySelectorAll('[data-make-logo]').forEach((entry) => {
      const title = entry.dataset.title?.trim().toLowerCase();
      if (!title || !entry.dataset.src) return;
      logos.set(title, {
        src: entry.dataset.src,
        alt: entry.dataset.alt || title,
        width: entry.dataset.width,
        height: entry.dataset.height,
      });
    });

    root.querySelectorAll('[data-make-logo-slot]').forEach((slot) => {
      const title = slot.dataset.makeLogoTitle?.trim().toLowerCase();
      const logo = title ? logos.get(title) : null;
      if (!logo) return;

      const img = document.createElement('img');
      img.src = logo.src;
      img.alt = logo.alt;
      img.className = 'mega-menu-tiered__make-logo';
      img.width = Number(logo.width) || 40;
      img.height = Number(logo.height) || 40;
      img.loading = 'lazy';
      slot.replaceWith(img);
    });
  }

  function setFilterVisibility(items, visible) {
    items.forEach((item) => {
      item.classList.toggle('is-filter-hidden', !visible);
      item.toggleAttribute('hidden', !visible);
    });
  }

  function buildFilterCache(root) {
    const makeItems = Array.from(root.querySelectorAll('[data-mobile-filter-make]'));
    const seriesItems = Array.from(root.querySelectorAll('[data-mobile-filter-series]'));
    const modelLinks = Array.from(root.querySelectorAll('[data-mobile-filter-model]'));

    const modelsByKey = new Map();
    root.querySelectorAll('[data-mobile-tiered-panel="models"]').forEach((panel) => {
      const key = `${panel.dataset.makeIndex}-${panel.dataset.seriesIndex}`;
      modelsByKey.set(
        key,
        modelLinks.filter((link) => link.closest('[data-mobile-tiered-panel="models"]') === panel)
      );
    });

    const cache = new Map();

    root.querySelectorAll('[data-mobile-tiered-open-makes]').forEach((typeRow) => {
      const typeValue = typeRow.dataset.typeValue || '';

      const makes = makeItems.map((item) => {
        const makeIndex = item.dataset.makeIndex;
        const seriesForMake = seriesItems.filter((series) => series.dataset.makeIndex === makeIndex);
        const visible = seriesForMake.some((series) => {
          const key = `${series.dataset.makeIndex}-${series.dataset.seriesIndex}`;
          return (modelsByKey.get(key) || []).some((link) => linkHasType(parseAvailableTypes(link), typeValue));
        });
        return { item, visible };
      });

      const series = seriesItems.map((item) => {
        const key = `${item.dataset.makeIndex}-${item.dataset.seriesIndex}`;
        const visible = (modelsByKey.get(key) || []).some((link) =>
          linkHasType(parseAvailableTypes(link), typeValue)
        );
        return { item, visible };
      });

      const models = modelLinks.map((link) => ({
        link,
        visible: linkHasType(parseAvailableTypes(link), typeValue),
      }));

      const linkHrefs = new Map();
      models.forEach(({ link, visible }) => {
        if (!visible) return;
        const types = parseAvailableTypes(link);
        const matched = types?.find((type) => typesMatch(typeValue, type)) || typeValue;
        const slug = typeValueToSlug(matched);
        const baseUrl = link.dataset.collectionUrl || link.getAttribute('href') || '';
        linkHrefs.set(link, buildCollectionTypeUrl(baseUrl, slug));
      });

      cache.set(typeValue, { makes, series, models, linkHrefs });
    });

    return cache;
  }

  function applyTypeFilter(root, typeValue) {
    const state = root._typeFilterCache?.get(typeValue);
    if (!state) return;

    setFilterVisibility(
      state.makes.map(({ item }) => item),
      true
    );
    setFilterVisibility(
      state.series.map(({ item }) => item),
      true
    );
    setFilterVisibility(
      state.models.map(({ link }) => link.closest('li')),
      true
    );

    state.makes.forEach(({ item, visible }) => setFilterVisibility([item], visible));
    state.series.forEach(({ item, visible }) => setFilterVisibility([item], visible));
    state.models.forEach(({ link, visible }) => setFilterVisibility([link.closest('li')], visible));

    state.linkHrefs.forEach((href, link) => link.setAttribute('href', href));
  }

  function resetMobileTieredCategory(root) {
    if (!root) return;
    const panels = Array.from(root.querySelectorAll('[data-mobile-tiered-panel]'));
    panels.forEach((panel) => {
      const isTypes = panel.dataset.mobileTieredPanel === 'types';
      panel.classList.toggle('is-active', isTypes);
      panel.toggleAttribute('hidden', !isTypes);
    });
    const scrollHost = root.closest('.simple-menu-content') || root.parentElement;
    if (scrollHost) scrollHost.scrollTop = 0;
    root._activeTypeValue = '';
  }

  function initMobileTieredCategory(root) {
    if (!root || root.dataset.mobileTieredCategoryInitialized === 'true') return;
    root.dataset.mobileTieredCategoryInitialized = 'true';

    const drawer = document.getElementById('Drawer-Menu');
    if (!drawer || drawer.classList.contains('active')) {
      applyMakeLogos(root);
    } else {
      const logoObserver = new MutationObserver(() => {
        if (!drawer.classList.contains('active')) return;
        logoObserver.disconnect();
        applyMakeLogos(root);
      });
      logoObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    let panels = Array.from(root.querySelectorAll('[data-mobile-tiered-panel]'));
    const typeHeading = root.querySelector('[data-mobile-category-type-label]');

    function refreshPanels() {
      hydrateDeferredPanels(root);
      applyMakeLogos(root);
      panels = Array.from(root.querySelectorAll('[data-mobile-tiered-panel]'));
    }

    function showPanel(panelName, makeIndex, seriesIndex) {
      if (panelName !== 'types') refreshPanels();
      panels.forEach((panel) => {
        const name = panel.dataset.mobileTieredPanel;
        let isActive = false;

        if (panelName === 'types') {
          isActive = name === 'types';
        } else if (panelName === 'makes') {
          isActive = name === 'makes';
        } else if (panelName === 'series') {
          isActive = name === 'series' && panel.dataset.makeIndex === String(makeIndex);
        } else if (panelName === 'models') {
          isActive =
            name === 'models' &&
            panel.dataset.makeIndex === String(makeIndex) &&
            panel.dataset.seriesIndex === String(seriesIndex);
        }

        panel.classList.toggle('is-active', isActive);
        panel.toggleAttribute('hidden', !isActive);
      });

      const scrollHost = root.closest('.simple-menu-content') || root.parentElement;
      if (scrollHost) scrollHost.scrollTop = 0;
    }

    function activateType(typeRow) {
      const typeValue = typeRow.dataset.typeValue || '';
      const typeTitle = typeRow.dataset.typeTitle || '';
      root._activeTypeValue = typeValue;

      if (typeHeading && typeTitle) {
        typeHeading.textContent = typeTitle;
      }

      refreshPanels();
      root._typeFilterCache = buildFilterCache(root);
      applyTypeFilter(root, typeValue);
      showPanel('makes');
    }

    root.addEventListener('click', (event) => {
      const openMakes = event.target.closest('[data-mobile-tiered-open-makes]');
      if (openMakes && root.contains(openMakes)) {
        event.preventDefault();
        activateType(openMakes);
        return;
      }

      const openSeries = event.target.closest('[data-mobile-tiered-open-series]');
      if (openSeries && root.contains(openSeries)) {
        event.preventDefault();
        const makeItem = openSeries.closest('[data-mobile-filter-make]');
        if (makeItem?.classList.contains('is-filter-hidden') || makeItem?.hasAttribute('hidden')) {
          return;
        }
        if (root._activeTypeValue) {
          applyTypeFilter(root, root._activeTypeValue);
        }
        showPanel('series', openSeries.dataset.makeIndex);
        return;
      }

      const openModels = event.target.closest('[data-mobile-tiered-open-models]');
      if (openModels && root.contains(openModels)) {
        event.preventDefault();
        const seriesItem = openModels.closest('[data-mobile-filter-series]');
        if (seriesItem?.classList.contains('is-filter-hidden') || seriesItem?.hasAttribute('hidden')) {
          return;
        }
        if (root._activeTypeValue) {
          applyTypeFilter(root, root._activeTypeValue);
        }
        showPanel('models', openModels.dataset.makeIndex, openModels.dataset.seriesIndex);
        return;
      }

      const back = event.target.closest('[data-mobile-tiered-back]');
      if (back && root.contains(back)) {
        event.preventDefault();
        const target = back.dataset.mobileTieredBack;
        if (target === 'types') {
          showPanel('types');
        } else if (target === 'makes') {
          showPanel('makes');
        } else if (target === 'series') {
          showPanel('series', back.dataset.makeIndex);
        }
      }
    });

    const typesUrl = root.dataset.vehicleTypesUrl;
    loadVehicleTypes(typesUrl).then(() => {
      if (!root.isConnected) return;
      root._typeFilterCache = buildFilterCache(root);
      if (root._activeTypeValue) {
        applyTypeFilter(root, root._activeTypeValue);
      }
    });
  }

  function initAll() {
    document.querySelectorAll('[data-mobile-tiered-category]').forEach(initMobileTieredCategory);
  }

  function resetAll() {
    document.querySelectorAll('[data-mobile-tiered-category]').forEach(resetMobileTieredCategory);
  }

  window.resetMobileTieredCategoryMenus = resetAll;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
